<template>
  <div
    v-show="isFullScreen"
    :style="fullScreenLines"
    class="d-flex"
    :class="isFullScreen ? 'justify-center' : ''"
  >
    <div
      class="d-flex flex-column card"
      :class="!isFullScreen ? 'mb-10' : ''"
      :style="cardStyles"
    >
      <!-- Header -->
      <header class="d-flex" :class="headerClasses" :style="headerStyles">
        <!-- Left side content (WhatsApp icon + username) -->
        <div class="d-flex align-center">
          <!-- WhatsApp icon (left side) -->
          <div v-if="isWhatsapp" class="pa-1" :style="iconWrapperStyles">
            <v-icon :size="iconSize" :style="iconColorStyles" class="custom-icon">
              {{ icon }}
            </v-icon>
          </div>
          <!-- Username -->
          <span
            class="font-weight-medium ml-3"
            :class="usernameTextClass"
            :style="usernameStyles"
          >
            {{ displayName }}
          </span>
        </div>

        <!-- Right side icons (Instagram/TikTok) -->
        <div v-if="hasRightIcon" class="pa-1 mr-3" :style="iconWrapperStyles">
          <!-- TikTok SVG -->
          <component
            v-if="isTikTok"
            :is="iconComponent"
            :style="iconColorStyles"
            class="custom-svg-icon"
          />
          <!-- Regular icon for other platforms -->
          <v-icon v-else :size="iconSize" :style="iconColorStyles" class="custom-icon">
            {{ icon }}
          </v-icon>
        </div>
      </header>

      <!-- Content Section -->
      <section class="d-flex align-center flex-grow-1 mx-1">
        <!-- Text Content -->
        <div
          v-if="isTextContent"
          v-html="colabText"
          class="px-6 font-weight-light"
          :style="textContentStyles"
        />

        <!-- Audio Content -->
        <div
          v-else-if="isAudioContent"
          class="d-flex align-center justify-center px-4"
          style="width: 100%"
        >
          <!-- Custom Audio Player -->
          <div class="custom-audio-player" :style="audioPlayerStyles">
            <!-- Hidden audio element with autoplay -->
            <audio
              ref="audioElement"
              :src="item.url_midia_colaboracao"
              @loadedmetadata="onAudioLoaded"
              @timeupdate="onTimeUpdate"
              @ended="onAudioEnded"
              @canplay="onCanPlay"
              autoplay
              preload="auto"
              style="display: none"
            />

            <!-- Audio Controls -->
            <div class="audio-controls">
              <!-- Play/Pause Button -->
              <button
                class="play-pause-btn"
                :style="playButtonStyles"
                @click="togglePlayPause"
              >
                <v-icon :style="playIconStyles">
                  {{ isPlaying ? "mdi-pause" : "mdi-play" }}
                </v-icon>
              </button>

              <!-- Wave Timeline -->
              <div class="wave-timeline-container" @click="seekAudio">
                <div class="wave-timeline" :style="waveTimelineStyles">
                  <!-- Wave bars -->
                  <div
                    v-for="i in 50"
                    :key="i"
                    class="wave-bar"
                    :style="getWaveBarStyle(i)"
                  ></div>
                  <!-- Progress overlay -->
                  <div class="wave-progress" :style="waveProgressStyles"></div>
                </div>
              </div>

              <!-- Speed Control Button -->
              <button
                class="speed-btn"
                :style="speedButtonStyles"
                @click="togglePlaybackSpeed"
              >
                <span :style="speedTextStyles"> {{ playbackSpeed }}x </span>
              </button>
            </div>
          </div>
        </div>

        <!-- Media Content (Image/Video) -->
        <div
          v-else-if="isMediaContent"
          :style="mediaWrapperStyles"
          :class="mediaWrapperClasses"
          :id="`${item.id_colaboracao}-wrapper`"
        >
          <!-- Image -->
          <img
            v-if="isImageContent"
            :id="`${item.id_colaboracao}`"
            :src="item.url_midia_colaboracao"
            :style="imageStyles"
            alt=""
          />

          <!-- Video -->
          <video
            v-if="isVideoContent"
            autoplay
            :id="`${item.id_colaboracao}`"
            :key="item.url_midia_colaboracao"
            style="justify-self: center"
            :style="videoStyles"
          >
            <source :src="item.url_midia_colaboracao" />
          </video>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import { formatTarjaText } from "@/utils/stringUtils";
import TikTokSvg from "@/assets/logo-tiktok.svg";

export default {
  props: {
    item: Object,
    isFullScreen: Boolean,
    posicaoCard: String,
    flg_exibir_uf: Boolean,
  },

  components: {
    TikTokSvg,
  },

  data() {
    return {
      dsc_telefone_producao: null,
      midiaOrientation: null,

      isPlaying: false,
      currentTime: 0,
      duration: 0,
      playbackSpeed: 1,
      availableSpeeds: [1, 1.5, 2],
      autoplayAttempted: false,

      percentScreenDim: {
        maxWidth: 40,
        minWidth: 20,
        minHeight: 30,
        maxHeight: 70,
        fullScreenMaxWidth: 60,
        fullScreenMaxHeight: 70,
        fullScreenMinWidth: 60,
        fullScreenMinHeight: 70,
      },

      // Platform-specific theme configurations
      platformThemes: {
        instagram: {
          colors: {
            username: "rgb(218,202,173)",
            bodyText: "rgb(82,32,0)",
            hashtagColor: "rgb(147, 38, 17)",
            iconBg: "rgb(82,32,0)",
            iconColor: "rgb(218,202,173)",
            cardBgDefault: "rgba(218,202,173, 1)",
            cardBgVertical: "rgb(147, 38, 17)",
            headerBgHorizontal: "rgb(255, 165, 174)",
            headerBgDefault: "rgb(82,33,0)",
            borderVertical: "rgb(147,38,17)",
            borderHorizontal: "rgb(254, 248, 200)",
          },
          layout: {
            iconPosition: "right",
            headerJustification: "space-between",
            cardWidth: "840px",
            cardHeight: "240px",
          },
          typography: {
            usernameFont: "Fields Medium",
            textFont: "Fields Regular",
            usernameSize: { fullScreen: "text-h3", normal: "text-h4" },
            textTransform: "lowercase",
          },
        },

        tiktok: {
          colors: {
            username: "rgb(188,148,171)",
            bodyText: "rgb(68,31,37)",
            hashtagColor: "rgb(142,3,46)",
            iconBg: "rgb(80,31,0)",
            iconColor: "rgb(193,159,193)",
            cardBgDefault: "rgb(149,121,159)",
            cardBgVertical: "rgb(149,121,159)",
            headerBgHorizontal: "rgb(81,32,0)",
            headerBgDefault: "rgb(80,31,0)",
            borderVertical: "rgb(255, 0, 80)",
            borderHorizontal: "rgb(255, 255, 255)",
          },
          layout: {
            iconPosition: "right",
            headerJustification: "space-between",
            cardWidth: "840px",
            cardHeight: "240px",
          },
          typography: {
            usernameFont: "Fields Medium",
            textFont: "Fields Regular",
            usernameSize: { fullScreen: "text-h3", normal: "text-h4" },
            textTransform: "lowercase",
          },
        },

        whatsapp: {
          colors: {
            username: "rgb(82,32,0)",
            bodyText: "rgb(82,32,0)",
            hashtagColor: "rgb(195,96,22)",
            iconBg: "rgb(82,32,0)",
            iconColor: "rgb(238,117,27)",
            cardBgDefault: "rgba(193,178,152,1)",
            cardBgVertical: "rgb(147, 38, 17)",
            headerBgHorizontal: "rgb(255, 165, 174)",
            headerBgDefault: "rgba(255,112,0,1)",
            borderVertical: "rgb(147, 38, 17)",
            borderHorizontal: "rgb(254, 248, 200)",
            // Audio player colors
            audioPlayerBg: "rgb(221,211,191)",
            audioControlsBg: "rgb(82,32,0)",
            audioControlsColor: "rgb(84,29,1)",
            audioSpeedButtonBg: "rgb(84,29,1)",
            audioSpeedButtonText: "rgb(254, 248, 200)",
            audioWaveColor: "rgb(236,116,27)",
            audioWaveProgressColor: "rgb(81,32,0)",
          },
          layout: {
            iconPosition: "left",
            headerJustification: "flex-start",
            cardWidth: "840px",
            cardHeight: "240px",
          },
          typography: {
            usernameFont: "Fields Regular",
            textFont: "Fields Regular",
            usernameSize: { fullScreen: "text-h3", normal: "text-h4" },
          },
        },
      },
    };
  },

  async created() {
    await this.getNumTelefoneProducao();
    if (this.item.url_midia_colaboracao) {
      this.getMidiaOrientation();
    }
  },

  mounted() {
    this.$nextTick(() => {
      if (this.isAudioContent && this.$refs.audioElement) {
        this.attemptAutoplay();
      }
    });
  },

  computed: {
    platformType() {
      const source = this.item.dsc_fonte_colaboracao?.toLowerCase();
      const platformMap = {
        instagram: "instagram",
        tiktok: "tiktok",
        whatsapp: "whatsapp",
      };
      return platformMap[source] || "whatsapp";
    },

    currentTheme() {
      return this.platformThemes[this.platformType] || this.platformThemes.whatsapp;
    },

    isInstagram() {
      return this.platformType === "instagram";
    },

    isTikTok() {
      return this.platformType === "tiktok";
    },

    isWhatsapp() {
      return this.platformType === "whatsapp";
    },

    hasRightIcon() {
      return this.isInstagram || this.isTikTok;
    },

    isTextContent() {
      return !this.item.dsc_tipo_midia_colaboracao;
    },

    isAudioContent() {
      return this.item.dsc_tipo_midia_colaboracao === "audio";
    },

    isMediaContent() {
      return (
        this.isFullScreen &&
        this.item.url_midia_colaboracao &&
        ["image", "video"].includes(this.item.dsc_tipo_midia_colaboracao)
      );
    },

    isImageContent() {
      return this.item.dsc_tipo_midia_colaboracao === "image";
    },

    isVideoContent() {
      return this.item.dsc_tipo_midia_colaboracao === "video";
    },

    displayName() {
      if (this.flg_exibir_uf && this.item.dsc_uf_colaborador) {
        return `${this.item.dsc_nome_colaborador} - ${this.item.dsc_uf_colaborador}`;
      }
      return this.item.dsc_nome_colaborador;
    },

    icon() {
      const iconMap = {
        instagram: "mdi-instagram",
        tiktok: "",
        whatsapp: "mdi-whatsapp",
      };
      return iconMap[this.platformType] || "mdi-whatsapp";
    },

    iconComponent() {
      const componentMap = {
        tiktok: "TikTokSvg",
      };
      return componentMap[this.platformType];
    },

    iconSize() {
      return this.isFullScreen ? "48" : "36";
    },

    colabText() {
      return formatTarjaText(this.item.dsc_texto_colaboracao, "papoDeSegunda");
    },

    audioPlayerStyles() {
      return {
        width: "100%",
        backgroundColor: this.currentTheme.colors.audioPlayerBg,
        borderRadius: "0px",
        padding: "15px",
      };
    },

    playButtonStyles() {
      return {
        border: "none",
        borderRadius: "50%",
        width: "40px",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        marginRight: "15px",
      };
    },

    playIconStyles() {
      return {
        color: `${this.currentTheme.colors.audioControlsColor} !important`,
        fontSize: "40px",
      };
    },

    speedButtonStyles() {
      return {
        backgroundColor: this.currentTheme.colors.audioSpeedButtonBg,
        border: "none",
        borderRadius: "20px",
        width: "70px",
        height: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        marginLeft: "15px",
      };
    },

    speedTextStyles() {
      return {
        color: this.currentTheme.colors.audioSpeedButtonText,
        fontSize: "18px",
        fontWeight: "400",
        fontFamily: "Fields",
      };
    },

    waveTimelineStyles() {
      return {
        backgroundColor: "transparent",
        borderRadius: "15px",
        height: "40px",
        position: "relative",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 5px",
        overflow: "hidden",
      };
    },

    waveProgressStyles() {
      const progressPercent =
        this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
      return {
        position: "absolute",
        top: "0",
        left: "0",
        height: "100%",
        width: `${progressPercent}%`,
        backgroundColor: `${this.currentTheme.colors.audioWaveProgressColor}33`,
        borderRadius: "15px",
        transition: "width 0.1s ease",
      };
    },

    bodyTextSize() {
      const numChars = this.item.dsc_texto_colaboracao.trim().length;
      const sizeMap = {
        40: "2.1rem",
        80: "2rem",
        120: "2rem",
        160: "2rem",
        200: "2rem",
        240: "1.9rem",
        280: "1.8rem",
      };

      for (const [threshold, size] of Object.entries(sizeMap)) {
        if (numChars <= threshold) return size;
      }
      return "1.5rem";
    },

    lineHeight() {
      const numChars = this.item.dsc_texto_colaboracao.trim().length;
      const lineHeightMap = {
        40: "2.6rem",
        80: "2.5rem",
        120: "2.4rem",
        160: "2.3rem",
        200: "2.2rem",
        240: "2.1rem",
        280: "1.95rem",
      };

      for (const [threshold, height] of Object.entries(lineHeightMap)) {
        if (numChars <= threshold) return height;
      }
      return "1.5rem";
    },

    screenDimensions() {
      return {
        width: this.$vuetify.breakpoint.width,
        height: this.$vuetify.breakpoint.height,
      };
    },

    headerClasses() {
      return this.currentTheme.layout.iconPosition === "right"
        ? "justify-space-between align-center"
        : "";
    },

    headerStyles() {
      const bgColor =
        this.midiaOrientation === "horizontal"
          ? this.currentTheme.colors.headerBgHorizontal
          : this.currentTheme.colors.headerBgDefault;
      return `background-color: ${bgColor};`;
    },

    iconWrapperStyles() {
      return {
        backgroundColor: this.currentTheme.colors.iconBg,
      };
    },

    iconColorStyles() {
      return {
        color: `${this.currentTheme.colors.iconColor} !important`,
        fill: `${this.currentTheme.colors.iconColor} !important`,
        width: this.isFullScreen ? "48px" : "36px",
        height: this.isFullScreen ? "48px" : "36px",
      };
    },

    usernameTextClass() {
      const sizeKey = this.isFullScreen ? "fullScreen" : "normal";
      return this.currentTheme.typography.usernameSize[sizeKey];
    },

    usernameStyles() {
      return {
        color: this.currentTheme.colors.username,
        fontFamily: `${this.currentTheme.typography.usernameFont} !important`,
        textTransform: this.currentTheme.typography.textTransform,
        textAlign: "center",
        display: "inline-block",
      };
    },

    textContentStyles() {
      return {
        color: this.currentTheme.colors.bodyText,
        fontFamily: `${this.currentTheme.typography.textFont} !important`,
        lineHeight: this.lineHeight,
        fontSize: this.bodyTextSize,
        "--hashtag-color": this.currentTheme.colors.hashtagColor,
      };
    },

    cardStyles() {
      if (this.isFullScreen) {
        return this.fullScreenCardStyles;
      }
      return this.defaultCardStyles;
    },

    fullScreenCardStyles() {
      const { width, height } = this.screenDimensions;
      const { percentScreenDim } = this;

      const minWidth = (percentScreenDim.fullScreenMinWidth * width) / 100;
      const maxWidth = (percentScreenDim.fullScreenMaxWidth * width) / 100 + 20;
      const minHeight = (percentScreenDim.fullScreenMinHeight * height) / 100;
      const maxHeight = (percentScreenDim.fullScreenMaxHeight * height) / 100 + 50;

      const backgroundColor =
        this.midiaOrientation === "vertical"
          ? this.currentTheme.colors.cardBgVertical
          : this.currentTheme.colors.cardBgDefault;

      return {
        justifySelf: "center",
        maxWidth: `${maxWidth}px !important`,
        maxHeight: `${maxHeight}px !important`,
        minWidth: `${minWidth}px !important`,
        minHeight: `${minHeight}px !important`,
        backgroundColor: backgroundColor,
      };
    },

    defaultCardStyles() {
      return {
        backgroundColor: this.currentTheme.colors.cardBgDefault,
        width: this.currentTheme.layout.cardWidth,
        height: this.currentTheme.layout.cardHeight,
      };
    },

    fullScreenLines() {
      if (this.isFullScreen) {
        const borderColor =
          this.midiaOrientation === "vertical"
            ? this.currentTheme.colors.borderVertical
            : this.currentTheme.colors.borderHorizontal;

        const width =
          (this.percentScreenDim.fullScreenMaxWidth * this.screenDimensions.width) / 100 +
          200;

        return {
          justifySelf: "center",
          width: `${width}px`,
          borderColor: borderColor,
          borderLeftWidth: "3px",
          borderRightWidth: "3px",
          borderTopWidth: "0px",
          borderBottomWidth: "0px",
          borderStyle: "solid",
        };
      }
      return this.cardPositionStyles;
    },

    cardPositionStyles() {
      const positionMap = {
        esquerda: !this.item.url_midia_colaboracao
          ? "margin-left: 100px; justify-self: flex-start"
          : "margin-left: 120px; justify-self: flex-start",
        centro: "justify-self: center",
        direita: !this.item.url_midia_colaboracao
          ? "margin-right: 40px; justify-self: flex-end"
          : "margin-right: 60px; justify-self: flex-start",
      };
      return positionMap[this.posicaoCard] || "";
    },

    mediaWrapperStyles() {
      if (this.isImageContent) {
        return { width: "100%" };
      }
      return {
        width: "100%",
        display: "grid",
        gridTemplateColumns: "1fr",
      };
    },

    mediaWrapperClasses() {
      return this.isImageContent ? "d-flex align-center justify-center pa-2" : "pa-2";
    },

    imageStyles() {
      const { width, height } = this.screenDimensions;
      const { percentScreenDim } = this;

      if (this.midiaOrientation === "horizontal") {
        return {
          maxWidth: `${(percentScreenDim.fullScreenMaxWidth * width) / 100}px`,
          minWidth: `${(percentScreenDim.fullScreenMinWidth * width) / 100}px`,
          maxHeight: `${(percentScreenDim.fullScreenMaxHeight * height) / 100 - 20}px`,
          minHeight: `${(percentScreenDim.fullScreenMinHeight * height) / 100 - 20}px`,
        };
      }
      return {
        maxWidth: `${(percentScreenDim.maxWidth * width) / 100 - 20}px`,
        maxHeight: `${(percentScreenDim.maxHeight * height) / 100 - 50}px`,
      };
    },

    videoStyles() {
      const { width, height } = this.screenDimensions;
      const { percentScreenDim } = this;

      if (this.midiaOrientation === "horizontal") {
        return {
          maxHeight: `${(percentScreenDim.fullScreenMaxHeight * height) / 100}px`,
          maxWidth: `${(percentScreenDim.fullScreenMaxWidth * width) / 100}px`,
        };
      }
      return {
        maxWidth: "100%",
        maxHeight: `${(percentScreenDim.maxHeight * height) / 100 - 50}px`,
      };
    },
  },

  methods: {
    async attemptAutoplay() {
      if (this.autoplayAttempted || !this.$refs.audioElement) return;

      this.autoplayAttempted = true;
      const audio = this.$refs.audioElement;

      try {
        await audio.play();
        this.isPlaying = true;
      } catch (error) {
        console.error("Autoplay prevented by browser:", error);
        this.isPlaying = false;
      }
    },

    togglePlayPause() {
      const audio = this.$refs.audioElement;
      if (this.isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      this.isPlaying = !this.isPlaying;
    },

    togglePlaybackSpeed() {
      const currentIndex = this.availableSpeeds.indexOf(this.playbackSpeed);
      const nextIndex = (currentIndex + 1) % this.availableSpeeds.length;
      this.playbackSpeed = this.availableSpeeds[nextIndex];

      if (this.$refs.audioElement) {
        this.$refs.audioElement.playbackRate = this.playbackSpeed;
      }
    },

    onAudioLoaded() {
      this.duration = this.$refs.audioElement.duration;
      this.$refs.audioElement.playbackRate = this.playbackSpeed;

      if (!this.autoplayAttempted) {
        this.attemptAutoplay();
      }
    },

    onCanPlay() {
      if (!this.autoplayAttempted) {
        this.attemptAutoplay();
      }
    },

    onTimeUpdate() {
      this.currentTime = this.$refs.audioElement.currentTime;
    },

    onAudioEnded() {
      this.isPlaying = false;
      this.currentTime = 0;
    },

    seekAudio(event) {
      const timeline = event.currentTarget;
      const rect = timeline.getBoundingClientRect();
      const percent = (event.clientX - rect.left) / rect.width;
      const seekTime = percent * this.duration;

      this.$refs.audioElement.currentTime = seekTime;
      this.currentTime = seekTime;
    },

    getWaveBarStyle(index) {
      const heights = [
        8,
        15,
        20,
        12,
        25,
        18,
        10,
        22,
        16,
        14,
        28,
        11,
        19,
        24,
        13,
        17,
        21,
        9,
        26,
        15,
      ];
      const height = heights[index % heights.length];

      const progressPercent =
        this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;
      const barProgress = (index / 50) * 100;

      const isActive = barProgress <= progressPercent;

      return {
        width: "2px",
        height: `${height}px`,
        backgroundColor: isActive
          ? this.currentTheme.colors.audioWaveProgressColor
          : this.currentTheme.colors.audioWaveColor,
        borderRadius: "1px",
        transition: "background-color 0.1s ease",
      };
    },

    async getNumTelefoneProducao() {
      const cod_producao = localStorage.getItem("@socialcam/selected_option_production");
      try {
        const response = await apiClient.get(`/listarDadosProducao/${cod_producao}`);
        this.dsc_telefone_producao = response.data.producao.dsc_num_telefone;
      } catch (error) {
        console.error(error);
      }
    },

    getMidiaOrientation() {
      if (!this.item.url_midia_colaboracao) return;

      if (this.isImageContent) {
        const image = new Image();
        image.src = this.item.url_midia_colaboracao;
        image.onload = () => {
          this.midiaDimension = { width: image.width, height: image.height };
          this.midiaOrientation = image.height > image.width ? "vertical" : "horizontal";
        };
      } else if (this.isVideoContent) {
        const video = document.createElement("video");
        video.src = this.item.url_midia_colaboracao;
        video.style.visibility = "hidden";
        document.body.appendChild(video);

        video.addEventListener("loadedmetadata", () => {
          this.midiaDimension = {
            width: video.videoWidth,
            height: video.videoHeight,
          };
          this.midiaOrientation =
            video.videoHeight > video.videoWidth ? "vertical" : "horizontal";
          document.body.removeChild(video);
        });
      }
    },

    updatePlatformTheme(platform, themeUpdates) {
      if (this.platformThemes[platform]) {
        this.platformThemes[platform] = {
          ...this.platformThemes[platform],
          ...themeUpdates,
        };
      }
    },
  },

  watch: {
    item: async function () {
      await this.getNumTelefoneProducao();
      if (this.item.url_midia_colaboracao) {
        this.getMidiaOrientation();
      }
      this.autoplayAttempted = false;
      this.$nextTick(() => {
        if (this.isAudioContent && this.$refs.audioElement) {
          this.attemptAutoplay();
        }
      });
    },

    cardStyles() {
      this.getMidiaOrientation();
    },

    isAudioContent(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          if (this.$refs.audioElement) {
            this.attemptAutoplay();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Fields Medium";
  src: url("@/assets/SaiaJusta/fonts/fields_medium.otf") format("opentype");
}

@font-face {
  font-family: "Fields Regular";
  src: url("@/assets/SaiaJusta/fonts/fields_regular.otf") format("opentype");
}

.card {
  border-radius: 1px;
}

// Custom icon styling
.custom-icon {
  ::v-deep .v-icon__svg,
  ::v-deep i {
    color: inherit !important;
  }
}

// Custom SVG icon styling
.custom-svg-icon {
  ::v-deep svg {
    fill: inherit !important;
    color: inherit !important;
  }

  ::v-deep path {
    fill: inherit !important;
    color: inherit !important;
  }
}

// Fix hashtag styling ONLY in text content area - be specific to avoid affecting header
section .px-6 ::v-deep span {
  color: var(--hashtag-color) !important;
  font-family: inherit !important;
  font-size: inherit !important;
  font-weight: inherit !important;
  line-height: inherit !important;
  text-transform: inherit !important;
}

// Custom Audio Player Styles
.custom-audio-player {
  .audio-controls {
    display: flex;
    align-items: center;
    width: 100%;

    .play-pause-btn,
    .speed-btn {
      transition: transform 0.1s ease;

      &:hover {
        transform: scale(1.05);
      }

      &:active {
        transform: scale(0.95);
      }
    }

    .speed-btn {
      transition: transform 0.1s ease, background-color 0.2s ease;

      &:hover {
        opacity: 1;
      }
    }

    .wave-timeline-container {
      flex: 1;
      margin: 0 15px;

      .wave-timeline {
        position: relative;

        .wave-bar {
          transition: background-color 0.2s ease, height 0.1s ease;

          &:hover {
            opacity: 1;
          }
        }

        .wave-progress {
          pointer-events: none;
        }
      }
    }

    .time-display {
      font-family: "Fields Medium", sans-serif;
      user-select: none;

      &.current-time {
        margin-right: 15px;
      }
    }
  }
}
</style>
